/**
 * Auto-Save Hook
 *
 * Provides debounced auto-save functionality for inline editing.
 * Tracks pending saves and shows save status to users.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

interface UseAutoSaveOptions<T, R = void> {
  /** Mutation function to save data */
  saveFn: (data: T) => Promise<R>;
  /** Query key(s) to invalidate after successful save */
  queryKey?: string | string[];
  /** Debounce delay in ms (default: 500) */
  debounceMs?: number;
  /** Success message (optional) */
  successMessage?: string;
  /** Error message (optional) */
  errorMessage?: string;
  /** Callback on success */
  onSuccess?: (result: R) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
}

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

interface UseAutoSaveResult<T> {
  /** Trigger a save with the given data (debounced) */
  save: (data: T) => void;
  /** Force an immediate save */
  saveNow: (data: T) => void;
  /** Cancel any pending save */
  cancel: () => void;
  /** Current save status */
  status: SaveStatus;
  /** Is currently saving */
  isSaving: boolean;
  /** Has pending changes */
  isPending: boolean;
  /** Last error */
  error: Error | null;
}

export function useAutoSave<T, R = void>(
  options: UseAutoSaveOptions<T, R>
): UseAutoSaveResult<T> {
  const {
    saveFn,
    queryKey,
    debounceMs = 500,
    successMessage,
    errorMessage = 'Failed to save',
    onSuccess,
    onError,
  } = options;

  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDataRef = useRef<T | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const mutation = useMutation({
    mutationFn: saveFn,
    onSuccess: (result) => {
      setStatus('saved');
      setError(null);
      if (successMessage) {
        message.success(successMessage);
      }
      if (queryKey) {
        const keys = Array.isArray(queryKey) ? queryKey : [queryKey];
        keys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: [key] });
        });
      }
      onSuccess?.(result);
      // Reset to idle after a short delay
      setTimeout(() => setStatus('idle'), 2000);
    },
    onError: (err: Error) => {
      setStatus('error');
      setError(err);
      message.error(errorMessage);
      onError?.(err);
    },
  });

  const saveNow = useCallback(
    (data: T) => {
      // Clear any pending debounced save
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      pendingDataRef.current = null;
      setStatus('saving');
      mutation.mutate(data);
    },
    [mutation]
  );

  const save = useCallback(
    (data: T) => {
      // Store pending data
      pendingDataRef.current = data;
      setStatus('pending');

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new debounced timeout
      timeoutRef.current = setTimeout(() => {
        if (pendingDataRef.current !== null) {
          setStatus('saving');
          mutation.mutate(pendingDataRef.current);
          pendingDataRef.current = null;
        }
        timeoutRef.current = null;
      }, debounceMs);
    },
    [mutation, debounceMs]
  );

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingDataRef.current = null;
    setStatus('idle');
  }, []);

  return {
    save,
    saveNow,
    cancel,
    status,
    isSaving: status === 'saving',
    isPending: status === 'pending',
    error,
  };
}

/**
 * Status indicator component for auto-save
 */
export function getSaveStatusText(status: SaveStatus): string {
  switch (status) {
    case 'pending':
      return 'Unsaved changes...';
    case 'saving':
      return 'Saving...';
    case 'saved':
      return 'Saved';
    case 'error':
      return 'Error saving';
    default:
      return '';
  }
}
